import React from 'react';
import AIChatBot from '../ui/widgets/aiChatbot.component';
import { renderableHtml } from '../objects/legacyHtml';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { lifeTaggerDesign } from '../design/tokens';

export const MediaItem = ({ item }) => {
  const source = item.url || item.assetPreviewUrl;

  if (item.type === 'image' && source) {
    return (
      <CardMedia
        component="img"
        image={source}
        alt={item.label || ''}
        sx={{ maxHeight: 520, objectFit: 'cover' }}
      />
    );
  }
  if (item.type === 'video' && source) {
    return (
      <Box
        component="video"
        controls
        src={source}
        sx={{ width: '100%', display: 'block' }}
      />
    );
  }
  if (item.type === 'audio' && source) {
    return (
      <Box component="audio" controls src={source} sx={{ width: '100%' }} />
    );
  }
  if (item.type === 'pdf' && source) {
    return (
      <Box
        component="iframe"
        src={source}
        title={item.label || 'PDF document'}
        sx={{ width: '100%', minHeight: 520, border: 0, display: 'block' }}
      />
    );
  }
  if (item.type === 'html' && item.body) {
    return (
      <Box
        sx={{ '& img, & video': { maxWidth: '100%' } }}
        dangerouslySetInnerHTML={{ __html: renderableHtml(item.body) }}
      />
    );
  }
  if (['url-embed', 'link', 'document', 'file'].includes(item.type) && source) {
    let safeLink = false;
    try {
      safeLink = ['http:', 'https:'].includes(
        new URL(source, 'https://dashboard.lifetaggerapp.com').protocol
      );
    } catch (_) {
      safeLink = false;
    }
    if (!safeLink) return <Typography>Link unavailable</Typography>;
    return (
      <Box component="a" href={source} target="_blank" rel="noreferrer">
        {item.label ||
          (['document', 'file'].includes(item.type)
            ? 'Open document'
            : 'Open link')}
      </Box>
    );
  }
  if (item.type === 'text' && item.body) {
    return (
      <Typography
        variant="body1"
        sx={{ whiteSpace: 'pre-wrap', fontSize: '1.05rem', lineHeight: 1.7 }}
      >
        {item.body}
      </Typography>
    );
  }
  return (
    <Typography color="text.secondary">
      {item.label || 'No preview is available for this content item.'}
    </Typography>
  );
};

export default function LifeObjectRenderer({
  lifeObject,
  compact = false,
  unframed = false,
}) {
  if (!lifeObject) return null;
  if (lifeObject.chatbot?.chatBotId && !compact) {
    return (
      <AIChatBot
        chatbotId={lifeObject.chatbot.chatBotId}
        preText={
          lifeObject.chatbot.initialPrompt || lifeObject.chatbot.greeting || ''
        }
        embedded
      />
    );
  }
  if (unframed) {
    return (
      <Box data-testid="authored-experience">
        {(lifeObject.content || []).map((item, index) => (
          <MediaItem key={`${item.type}-${index}`} item={item} />
        ))}
      </Box>
    );
  }
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: lifeTaggerDesign.color.border,
        borderRadius: `${lifeTaggerDesign.radius.card}px`,
        overflow: 'hidden',
        bgcolor: lifeTaggerDesign.color.paper,
        fontFamily: lifeTaggerDesign.type.body,
      }}
    >
      <CardContent sx={{ p: compact ? 2 : 4 }}>
        <Stack spacing={compact ? 2 : 3}>
          <Box>
            <Typography
              variant={compact ? 'h5' : 'h3'}
              sx={{
                fontFamily: lifeTaggerDesign.type.headline,
                color: lifeTaggerDesign.color.ink,
              }}
            >
              {lifeObject.name}
            </Typography>
            {lifeObject.owner?.name && (
              <Typography color="text.secondary">
                From {lifeObject.owner.name}
              </Typography>
            )}
          </Box>
          {lifeObject.content.map((item, index) => (
            <Box key={`${item.type}-${index}`}>
              <MediaItem item={item} />
            </Box>
          ))}
          {lifeObject.tags?.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {lifeObject.tags.map((tag) => (
                <Chip key={tag} size="small" label={tag} />
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
